import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, CircularProgress,
  Menu, MenuItem, Divider, LinearProgress,
} from '@mui/material';
import {
  EmojiEvents, Add, PersonAdd,
  MoreVert, IosShare, Delete,
} from '@mui/icons-material';
import { apiFetch, getToken } from './api';
import { TYPE_META, BADGE_COLORS } from './constants';
import Achievements from './Achievements';
import { PageHeader, SectionTitle, ListRow, ConfirmDialog, useToast } from '../Components/ui';
import { radius } from '../theme';

const fmtDate = (v) => { try { return new Date(v).toLocaleDateString('de-DE'); } catch { return '–'; } };

const ChallengeCard = ({ challenge, onEnd }) => {
  const meta = TYPE_META[challenge.type];
  const total = challenge.myMetric + challenge.opponentMetric;
  const myShare = total > 0 ? (challenge.myMetric / total) * 100 : 50;
  const leading = challenge.myMetric === challenge.opponentMetric ? null : challenge.myMetric > challenge.opponentMetric;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: radius.xl, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {meta.icon}
          <Typography variant="body2" fontWeight={700} noWrap>
            {meta.label} vs. {challenge.opponent.email}
          </Typography>
        </Box>
        <Button size="small" onClick={onEnd} sx={{ flexShrink: 0 }}>
          Beenden
        </Button>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={leading === true ? 700 : 400}>Du: {challenge.myMetric}</Typography>
        <Typography variant="body2" fontWeight={leading === false ? 700 : 400}>{challenge.opponentMetric}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={myShare} sx={{ height: 8, borderRadius: radius.xs }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Seit {fmtDate(challenge.startedAt)}
      </Typography>
    </Box>
  );
};

const AwardsList = ({ awards }) => {
  if (awards.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2">
        Noch keine Awards – gewinne eine Challenge, um dein erstes Abzeichen zu holen.
      </Typography>
    );
  }
  const byOpponent = awards.reduce((acc, a) => {
    (acc[a.opponent.email] ||= []).push(a);
    return acc;
  }, {});
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {Object.entries(byOpponent).map(([email, list]) => (
        <Box key={email} sx={{ border: 1, borderColor: 'divider', borderRadius: radius.xl, p: 2 }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>vs. {email}</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {list.map((a) => (
              <Chip
                key={a.type}
                icon={TYPE_META[a.type].icon}
                label={`${TYPE_META[a.type].label}: ${a.badge} (${a.winCount}x)`}
                sx={{ bgcolor: BADGE_COLORS[a.badge], color: 'common.white', fontWeight: 600, '& .MuiChip-icon': { color: 'common.white' } }}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default function Challenge() {
  const [tab, setTab] = useState(0);
  const [userId, setUserId] = useState(null);

  const [friends, setFriends] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  const [newChallengeFriend, setNewChallengeFriend] = useState(null);
  const [endTarget, setEndTarget] = useState(null);
  const [endResult, setEndResult] = useState(null);
  const [friendMenu, setFriendMenu] = useState({ el: null, friend: null });
  const { showToast } = useToast();

  useEffect(() => {
    try {
      const payload = JSON.parse(atob((getToken() || '').split('.')[1]));
      setUserId(payload.id);
    } catch { /* nicht eingeloggt */ }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, cRes] = await Promise.all([
        apiFetch('/api/challenges/friends'),
        apiFetch('/api/challenges/active'),
      ]);
      setFriends(fRes.ok ? await fRes.json() : []);
      setChallenges(cRes.ok ? await cRes.json() : []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAwards = useCallback(async () => {
    const r = await apiFetch('/api/challenges/awards');
    setAwards(r.ok ? await r.json() : []);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (tab === 1) loadAwards(); }, [tab, loadAwards]);

  // ── Einladung ────────────────────────────────────────────────────────────
  const createInvite = async () => {
    setInviteLoading(true);
    try {
      const res = await apiFetch('/api/challenges/invite/create', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setInviteLink(`${window.location.origin}/invite/${data.code}`);
      } else {
        showToast('Einladung konnte nicht erstellt werden.', { severity: 'error' });
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const openInviteDialog = () => { setInviteLink(''); setInviteOpen(true); createInvite(); };

  const shareInvite = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'NoBNoHungry', text: 'Fordere mich raus! 💪', url: inviteLink });
        return;
      } catch { /* abgebrochen */ }
    }
    navigator.clipboard?.writeText(inviteLink);
    showToast('Link kopiert!');
  };

  // ── Freunde ──────────────────────────────────────────────────────────────
  const removeFriend = async (friendId) => {
    setFriendMenu({ el: null, friend: null });
    await apiFetch(`/api/challenges/friends/${friendId}`, { method: 'DELETE' });
    loadAll();
  };

  const activeTypesFor = (friendId) =>
    challenges.filter((c) => c.opponent.id === friendId).map((c) => c.type);

  // ── Challenges ───────────────────────────────────────────────────────────
  const startChallenge = async (type) => {
    if (!newChallengeFriend) return;
    const res = await apiFetch('/api/challenges/create', {
      method: 'POST',
      body: JSON.stringify({ opponentId: newChallengeFriend.id, type }),
    });
    if (res.ok) {
      setNewChallengeFriend(null);
      loadAll();
    } else {
      const data = await res.json().catch(() => ({}));
      showToast(data.error || 'Challenge konnte nicht gestartet werden.', { severity: 'error' });
    }
  };

  const confirmEndChallenge = async () => {
    if (!endTarget) return;
    const res = await apiFetch(`/api/challenges/${endTarget.id}/end`, { method: 'POST' });
    if (res.ok) {
      const data = await res.json();
      setEndResult({ ...data, challenge: endTarget });
      loadAll();
    } else {
      showToast('Challenge konnte nicht beendet werden.', { severity: 'error' });
    }
    setEndTarget(null);
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 2, pb: 14 }}>
      <PageHeader title="Challenge" sx={{ mb: 1 }} />

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }} variant="fullWidth">
        <Tab label="Challenges" />
        <Tab label="Awards" />
      </Tabs>

      {tab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <SectionTitle>Laufende Challenges</SectionTitle>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : challenges.length === 0 ? (
              <Typography color="text.secondary" variant="body2">Noch keine laufende Challenge.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {challenges.map((c) => (
                  <ChallengeCard key={c.id} challenge={c} onEnd={() => setEndTarget(c)} />
                ))}
              </Box>
            )}
          </Box>

          <Divider />

          <Box>
            <SectionTitle
              action={
                <Button size="small" startIcon={<PersonAdd fontSize="small" />} onClick={openInviteDialog}>
                  Einladen
                </Button>
              }
            >
              Deine Freunde
            </SectionTitle>
            {friends.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                Noch keine Freunde – lade jemanden über einen Link ein.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {friends.map((f) => (
                  <ListRow
                    key={f.id}
                    title={f.email}
                    trailing={
                      <>
                        <Button
                          size="small" variant="outlined" startIcon={<Add fontSize="small" />}
                          onClick={() => setNewChallengeFriend(f)}
                          sx={{ flexShrink: 0 }}
                        >
                          Challenge
                        </Button>
                        <IconButton size="small" onClick={(e) => setFriendMenu({ el: e.currentTarget, friend: f })}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </>
                    }
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <SectionTitle>Deine Erfolge</SectionTitle>
            <Achievements />
          </Box>
          <Divider />
          <Box>
            <SectionTitle>Gegen Freunde</SectionTitle>
            <AwardsList awards={awards} />
          </Box>
        </Box>
      )}

      {/* Freund-Menü */}
      <Menu anchorEl={friendMenu.el} open={Boolean(friendMenu.el)} onClose={() => setFriendMenu({ el: null, friend: null })}>
        <MenuItem onClick={() => removeFriend(friendMenu.friend?.id)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1.5 }} /> Freundschaft beenden
        </MenuItem>
      </Menu>

      {/* Einladungs-Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight="bold">Freund einladen</DialogTitle>
        <DialogContent>
          {inviteLoading || !inviteLink ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Teile diesen Link – sobald er geöffnet wird, seid ihr Freunde und könnt Challenges starten.
              </Typography>
              <TextField fullWidth value={inviteLink} size="small" slotProps={{ htmlInput: { readOnly: true } }} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setInviteOpen(false)} variant="outlined" sx={{ flex: 1 }}>
            Schließen
          </Button>
          <Button
            onClick={shareInvite} variant="contained" disabled={!inviteLink}
            startIcon={<IosShare fontSize="small" />} sx={{ flex: 1 }}
          >
            Teilen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Neue Challenge */}
      <Dialog open={Boolean(newChallengeFriend)} onClose={() => setNewChallengeFriend(null)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight="bold">Challenge gegen {newChallengeFriend?.email}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            {Object.entries(TYPE_META).map(([type, meta]) => {
              const already = newChallengeFriend && activeTypesFor(newChallengeFriend.id).includes(type);
              return (
                <Button
                  key={type} variant="outlined" disabled={already}
                  startIcon={meta.icon} onClick={() => startChallenge(type)}
                  sx={{ justifyContent: 'flex-start', py: 1.25 }}
                >
                  {meta.label}{already ? ' – läuft bereits' : ''}
                </Button>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button fullWidth onClick={() => setNewChallengeFriend(null)} variant="outlined">
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Beenden-Bestätigung */}
      <ConfirmDialog
        open={Boolean(endTarget)}
        onClose={() => setEndTarget(null)}
        onConfirm={confirmEndChallenge}
        title="Challenge beenden?"
        message="Der aktuelle Stand entscheidet über den Sieger. Das kann nicht rückgängig gemacht werden."
        confirmLabel="Beenden"
        destructive
      />

      {/* Ergebnis */}
      <Dialog open={Boolean(endResult)} onClose={() => setEndResult(null)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight="bold">Ergebnis</DialogTitle>
        <DialogContent>
          {endResult && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <EmojiEvents sx={{ fontSize: 56, color: endResult.winnerId ? BADGE_COLORS.Gold : 'text.disabled', mb: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                {!endResult.winnerId
                  ? 'Unentschieden!'
                  : endResult.winnerId === userId
                    ? 'Du hast gewonnen! 🎉'
                    : `${endResult.challenge.opponent.email} hat gewonnen`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {endResult.creatorMetric} : {endResult.opponentMetric}
              </Typography>
              {endResult.award && (
                <Chip
                  sx={{ mt: 2, bgcolor: BADGE_COLORS[endResult.award.badge], color: 'common.white', fontWeight: 700 }}
                  label={`${endResult.award.badge} · ${endResult.award.winCount}x`}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button fullWidth variant="contained" onClick={() => setEndResult(null)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
